/**
 * Event Bus pentru comunicarea între componente
 * Permite actualizarea automată a statusurilor între pagini
 */

type EventCallback = (data: any) => void;

class EventBus {
  private events: Map<string, EventCallback[]> = new Map();
  private isListening = false;

  constructor() {
    this.setupStorageListener();
  }

  // Setup listener pentru localStorage events (cross-browser sync)
  private setupStorageListener() {
    if (typeof window !== 'undefined' && !this.isListening) {
      window.addEventListener('storage', (e) => {
        if (e.key === 'eventBus_event' && e.newValue) {
          try {
            const eventData = JSON.parse(e.newValue);
            // Emite evenimentul local în acest browser/tab
            const callbacks = this.events.get(eventData.type);
            if (callbacks) {
              callbacks.forEach(callback => callback(eventData.data));
            }
          } catch (error) {
            console.warn('Error parsing storage event:', error);
          }
        }
      });
      this.isListening = true;
    }
  }

  // Abonează la un eveniment
  on(event: string, callback: EventCallback) {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(callback);
  }

  // Dezabonează de la un eveniment
  off(event: string, callback: EventCallback) {
    const callbacks = this.events.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Emite un eveniment
  emit(event: string, data?: any) {
    // Emite local în același browser/tab
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }

    // Emite și în localStorage pentru sincronizarea între browsere/tab-uri
    try {
      const eventData = {
        type: event,
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem('eventBus_event', JSON.stringify(eventData));
      // Șterge imediat pentru a nu polua localStorage
      localStorage.removeItem('eventBus_event');
    } catch (error) {
      console.warn('Could not emit event to localStorage:', error);
    }
  }

  // Curăță toate evenimentele
  clear() {
    this.events.clear();
  }
}

// Instanță globală
export const eventBus = new EventBus();

// Tipuri de evenimente
export const EVENTS = {
  STUDY_STATUS_UPDATED: 'study_status_updated',
  REPORT_CREATED: 'report_created',
  REPORT_FINALIZED: 'report_finalized',
  REPORT_AMENDED: 'report_amended',
  REPORT_DELETED: 'report_deleted'
} as const;
